import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase, fetchWithTimeout } from "../../lib/supabase";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import ProductCard from "../../components/ProductCard";
import AdvancedFilters from "./components/AdvancedFilters";
import type { Product } from "../../types/product";
import { verifiedProducts } from "@/mocks/products";

// Kategori eşleştirme tablosu: URL slug -> Veritabanı kategorileri ve arama terimleri
const CATEGORY_MAPPING: Record<string, { field?: string; values?: string[]; searchKeyword?: string; categoryValues?: string[]; parentCategory?: string }> = {
  // Ana Kategoriler
  kadin: { field: "cinsiyet", values: ["Kadın"], categoryValues: ["Kadın", "Giyim", "Elbise", "Ayakkabı", "Çanta", "Aksesuar"] },
  erkek: { field: "cinsiyet", values: ["Erkek"], categoryValues: ["Erkek", "Giyim", "Gömlek", "Tişört", "Pantolon", "Ayakkabı"] },
  "anne-cocuk": { categoryValues: ["Anne & Çocuk", "Bebek", "Çocuk", "Oyuncak"] },
  "ayakkabi-canta": { categoryValues: ["Ayakkabı", "Çanta", "Sneaker", "Spor Ayakkabı"] },
  ayakkabi: { categoryValues: ["Ayakkabı", "Sneaker", "Spor Ayakkabı", "Bot"] },
  canta: { categoryValues: ["Çanta", "Sırt Çantası", "Omuz Çantası"] },
  kozmetik: { categoryValues: ["Kozmetik", "Parfüm", "Bakım", "Makyaj"] },
  spor: { categoryValues: ["Spor", "Spor Giyim", "Spor Ayakkabı", "Outdoor"] },
  elektronik: { categoryValues: ["Elektronik", "Telefon", "Bilgisayar", "Kulaklık", "Saat", "Robot Süpürge", "TV"] },
  "ev-yasam": { categoryValues: ["Ev & Yaşam", "Mutfak", "Ev Tekstili", "Dekorasyon"] },
  giyim: { categoryValues: ["Giyim", "Gömlek", "Tişört", "Pantolon", "Elbise", "Mont"] },
  aksesuar: { categoryValues: ["Aksesuar", "Saat", "Gözlük", "Kemer", "Cüzdan", "Takı"] },
  teknoloji: { categoryValues: ["Teknoloji", "Elektronik", "Telefon", "Bilgisayar"] },

  // Kadın Alt Kategorileri
  "kadin-elbise": { categoryValues: ["Elbise", "Giyim"], searchKeyword: "elbise", parentCategory: "kadin" },
  "kadin-tisort": { categoryValues: ["Tişört", "Giyim"], searchKeyword: "tişört", parentCategory: "kadin" },
  "kadin-gomlek": { categoryValues: ["Gömlek", "Bluz", "Giyim"], searchKeyword: "gömlek", parentCategory: "kadin" },
  "kadin-sweat": { categoryValues: ["Sweatshirt", "Giyim"], searchKeyword: "sweatshirt", parentCategory: "kadin" },
  "kadin-kazak": { categoryValues: ["Kazak", "Hırka", "Giyim"], searchKeyword: "kazak", parentCategory: "kadin" },
  "kadin-mont": { categoryValues: ["Mont", "Kaban", "Dış Giyim", "Giyim"], searchKeyword: "mont", parentCategory: "kadin" },
  "kadin-pantolon": { categoryValues: ["Pantolon", "Giyim"], searchKeyword: "pantolon", parentCategory: "kadin" },
  "kadin-jean": { categoryValues: ["Jean", "Pantolon", "Giyim"], searchKeyword: "jean", parentCategory: "kadin" },
  "kadin-etek": { categoryValues: ["Etek", "Giyim"], searchKeyword: "etek", parentCategory: "kadin" },
  "kadin-spor-ayakkabi": { categoryValues: ["Ayakkabı", "Spor Ayakkabı", "Sneaker"], searchKeyword: "spor ayakkabı", parentCategory: "kadin" },
  "kadin-sneaker": { categoryValues: ["Sneaker", "Ayakkabı"], searchKeyword: "sneaker", parentCategory: "kadin" },
  "kadin-topuklu": { categoryValues: ["Topuklu Ayakkabı", "Ayakkabı"], searchKeyword: "topuklu", parentCategory: "kadin" },
  "kadin-bot": { categoryValues: ["Bot", "Çizme", "Ayakkabı"], searchKeyword: "bot", parentCategory: "kadin" },
  "kadin-sandalet": { categoryValues: ["Sandalet", "Terlik", "Ayakkabı"], searchKeyword: "sandalet", parentCategory: "kadin" },
  "kadin-canta": { categoryValues: ["Çanta", "Aksesuar"], searchKeyword: "çanta", parentCategory: "kadin" },
  "kadin-saat": { categoryValues: ["Saat", "Aksesuar"], searchKeyword: "saat", parentCategory: "kadin" },
  "kadin-taki": { categoryValues: ["Takı", "Bijuteri", "Aksesuar"], searchKeyword: "takı", parentCategory: "kadin" },
  "kadin-gozluk": { categoryValues: ["Gözlük", "Güneş Gözlüğü", "Aksesuar"], searchKeyword: "gözlük", parentCategory: "kadin" },

  // Erkek Alt Kategorileri
  "erkek-tisort": { categoryValues: ["Tişört", "Giyim"], searchKeyword: "tişört", parentCategory: "erkek" },
  "erkek-gomlek": { categoryValues: ["Gömlek", "Giyim"], searchKeyword: "gömlek", parentCategory: "erkek" },
  "erkek-sweat": { categoryValues: ["Sweatshirt", "Giyim"], searchKeyword: "sweatshirt", parentCategory: "erkek" },
  "erkek-kazak": { categoryValues: ["Kazak", "Giyim"], searchKeyword: "kazak", parentCategory: "erkek" },
  "erkek-mont": { categoryValues: ["Mont", "Kaban", "Dış Giyim", "Giyim"], searchKeyword: "mont", parentCategory: "erkek" },
  "erkek-pantolon": { categoryValues: ["Pantolon", "Giyim"], searchKeyword: "pantolon", parentCategory: "erkek" },
  "erkek-jean": { categoryValues: ["Jean", "Pantolon", "Giyim"], searchKeyword: "jean", parentCategory: "erkek" },
  "erkek-ceket": { categoryValues: ["Ceket", "Giyim"], searchKeyword: "ceket", parentCategory: "erkek" },
  "erkek-takim": { categoryValues: ["Takım Elbise", "Giyim"], searchKeyword: "takım", parentCategory: "erkek" },
  "erkek-spor-ayakkabi": { categoryValues: ["Ayakkabı", "Spor Ayakkabı", "Sneaker"], searchKeyword: "spor ayakkabı", parentCategory: "erkek" },
  "erkek-sneaker": { categoryValues: ["Sneaker", "Ayakkabı"], searchKeyword: "sneaker", parentCategory: "erkek" },
  "erkek-bot": { categoryValues: ["Bot", "Ayakkabı"], searchKeyword: "bot", parentCategory: "erkek" },
  "erkek-saat": { categoryValues: ["Saat", "Aksesuar"], searchKeyword: "saat", parentCategory: "erkek" },
  "erkek-canta": { categoryValues: ["Çanta", "Sırt Çantası", "Aksesuar"], searchKeyword: "çanta", parentCategory: "erkek" },
  "erkek-gozluk": { categoryValues: ["Gözlük", "Güneş Gözlüğü", "Aksesuar"], searchKeyword: "gözlük", parentCategory: "erkek" },

  // Elektronik Alt Kategorileri
  "cep-telefonu": { categoryValues: ["Elektronik", "Telefon"], searchKeyword: "telefon", parentCategory: "elektronik" },
  "bluetooth-kulaklik": { categoryValues: ["Elektronik", "Kulaklık"], searchKeyword: "kulaklık", parentCategory: "elektronik" },
  "akilli-saat": { categoryValues: ["Elektronik", "Saat", "Akıllı Saat"], searchKeyword: "akıllı saat", parentCategory: "elektronik" },
  laptop: { categoryValues: ["Elektronik", "Bilgisayar", "Laptop"], searchKeyword: "laptop", parentCategory: "elektronik" },
  tablet: { categoryValues: ["Elektronik", "Tablet"], searchKeyword: "tablet", parentCategory: "elektronik" },
  televizyon: { categoryValues: ["Elektronik", "TV", "Televizyon"], searchKeyword: "televizyon", parentCategory: "elektronik" },
  "robot-supurge": { categoryValues: ["Elektronik", "Robot Süpürge", "Ev Elektroniği"], searchKeyword: "süpürge", parentCategory: "elektronik" },
  "kahve-makinesi": { categoryValues: ["Elektronik", "Mutfak"], searchKeyword: "kahve", parentCategory: "elektronik" },
  airfryer: { categoryValues: ["Elektronik", "Mutfak"], searchKeyword: "airfryer", parentCategory: "elektronik" },

  // Anne Çocuk Alt Kategorileri
  "bebek-giyim": { categoryValues: ["Anne & Çocuk", "Bebek", "Giyim"], searchKeyword: "bebek", parentCategory: "anne-cocuk" },
  "bebek-arabasi": { categoryValues: ["Anne & Çocuk", "Bebek Arabası"], searchKeyword: "bebek arabası", parentCategory: "anne-cocuk" },
  "kiz-cocuk": { categoryValues: ["Anne & Çocuk", "Çocuk", "Giyim"], searchKeyword: "kız çocuk", parentCategory: "anne-cocuk" },
  "erkek-cocuk": { categoryValues: ["Anne & Çocuk", "Çocuk", "Giyim"], searchKeyword: "erkek çocuk", parentCategory: "anne-cocuk" },
  oyuncak: { categoryValues: ["Anne & Çocuk", "Oyuncak"], searchKeyword: "oyuncak", parentCategory: "anne-cocuk" },

  // Kozmetik Alt Kategorileri
  parfum: { categoryValues: ["Kozmetik", "Parfüm"], searchKeyword: "parfüm", parentCategory: "kozmetik" },
  "cilt-bakimi": { categoryValues: ["Kozmetik", "Bakım"], searchKeyword: "krem", parentCategory: "kozmetik" },
  makyaj: { categoryValues: ["Kozmetik", "Makyaj"], searchKeyword: "makyaj", parentCategory: "kozmetik" },
};

// Kategori başlıkları ve İkonları
const CATEGORY_TITLES: Record<string, string> = {
  kadin: "Kadın Giyim & Moda",
  erkek: "Erkek Giyim & Moda",
  "anne-cocuk": "Anne & Bebek & Çocuk",
  "ayakkabi-canta": "Ayakkabı & Çanta",
  kozmetik: "Kozmetik & Kişisel Bakım",
  spor: "Spor & Outdoor",
  elektronik: "Elektronik & Teknoloji",
  "ev-yasam": "Ev & Yaşam Fırsatları",
  giyim: "Tüm Giyim Ürünleri",
  ayakkabi: "Ayakkabı & Sneaker",
  canta: "Çanta & Sırt Çantası",
  aksesuar: "Aksesuar & Takı & Saat",
  teknoloji: "Teknoloji & Cihazlar",

  "kadin-elbise": "Kadın Elbise Fırsatları",
  "kadin-tisort": "Kadın Tişört & Üst Giyim",
  "kadin-gomlek": "Kadın Gömlek & Bluz",
  "kadin-sweat": "Kadın Sweatshirt & Hoodie",
  "kadin-kazak": "Kadın Kazak & Hırka",
  "kadin-mont": "Kadın Mont & Kaban",
  "kadin-pantolon": "Kadın Pantolon Modelleri",
  "kadin-jean": "Kadın Jean & Kot",
  "kadin-etek": "Kadın Etek Modelleri",
  "kadin-spor-ayakkabi": "Kadın Spor Ayakkabı",
  "kadin-sneaker": "Kadın Sneaker Koleksiyonu",
  "kadin-topuklu": "Kadın Topuklu Ayakkabı",
  "kadin-bot": "Kadın Bot & Çizme",
  "kadin-canta": "Kadın Çanta Modelleri",
  "kadin-saat": "Kadın Kol Saati",
  "kadin-taki": "Kadın Takı & Bijuteri",
  "kadin-gozluk": "Kadın Güneş Gözlüğü",

  "erkek-tisort": "Erkek Tişört Modelleri",
  "erkek-gomlek": "Erkek Gömlek Fırsatları",
  "erkek-sweat": "Erkek Sweatshirt & Hoodie",
  "erkek-kazak": "Erkek Kazak & Triko",
  "erkek-mont": "Erkek Mont & Kaban",
  "erkek-pantolon": "Erkek Pantolon",
  "erkek-jean": "Erkek Jean & Kot",
  "erkek-ceket": "Erkek Ceket & Blazer",
  "erkek-takim": "Erkek Takım Elbise",
  "erkek-spor-ayakkabi": "Erkek Spor Ayakkabı",
  "erkek-sneaker": "Erkek Sneaker",
  "erkek-bot": "Erkek Bot & Outdoor",
  "erkek-saat": "Erkek Kol Saati",
  "erkek-canta": "Erkek Çanta & Sırt Çantası",
  "erkek-gozluk": "Erkek Güneş Gözlüğü",

  "cep-telefonu": "Akıllı Cep Telefonları",
  "bluetooth-kulaklik": "Kablosuz Bluetooth Kulaklık",
  "akilli-saat": "Akıllı Saat & Bileklik",
  laptop: "Dizüstü Bilgisayar & Laptop",
  tablet: "Tablet Modelleri",
  televizyon: "Televizyon & Ses Sistemleri",
  "robot-supurge": "Akıllı Robot Süpürgeler",
  "kahve-makinesi": "Kahve Makineleri",
  airfryer: "Airfryer & Sıcak Hava Fritözleri",

  "bebek-giyim": "Bebek Giyim & Kıyafet",
  "bebek-arabasi": "Bebek Arabası & Taşıma",
  "kiz-cocuk": "Kız Çocuk Giyim",
  "erkek-cocuk": "Erkek Çocuk Giyim",
  oyuncak: "Oyuncak & Eğitici Setler",

  parfum: "Parfüm & Deodorant",
  "cilt-bakimi": "Cilt Bakımı & Güneş Kremi",
  makyaj: "Makyaj & Kozmetik",
};

// Üst Hızlı Alt Kategori Çipleri Listesi
const QUICK_SUBCATEGORIES: Record<string, { label: string; slug: string; icon: string }[]> = {
  kadin: [
    { label: "Tümü", slug: "kadin", icon: "✨" },
    { label: "Elbise", slug: "kadin-elbise", icon: "👗" },
    { label: "Gömlek & Bluz", slug: "kadin-gomlek", icon: "👚" },
    { label: "Tişört", slug: "kadin-tisort", icon: "👕" },
    { label: "Pantolon & Jean", slug: "kadin-pantolon", icon: "👖" },
    { label: "Spor Ayakkabı", slug: "kadin-spor-ayakkabi", icon: "👟" },
    { label: "Çanta", slug: "kadin-canta", icon: "👜" },
    { label: "Mont & Kaban", slug: "kadin-mont", icon: "🧥" },
    { label: "Takı & Saat", slug: "kadin-taki", icon: "💍" },
  ],
  erkek: [
    { label: "Tümü", slug: "erkek", icon: "✨" },
    { label: "Gömlek", slug: "erkek-gomlek", icon: "👔" },
    { label: "Tişört", slug: "erkek-tisort", icon: "👕" },
    { label: "Pantolon & Jean", slug: "erkek-pantolon", icon: "👖" },
    { label: "Spor Ayakkabı", slug: "erkek-spor-ayakkabi", icon: "👟" },
    { label: "Sweatshirt", slug: "erkek-sweat", icon: "🧥" },
    { label: "Mont & Kaban", slug: "erkek-mont", icon: "🧥" },
    { label: "Saat", slug: "erkek-saat", icon: "⌚" },
    { label: "Çanta", slug: "erkek-canta", icon: "🎒" },
  ],
  elektronik: [
    { label: "Tümü", slug: "elektronik", icon: "✨" },
    { label: "Telefon", slug: "cep-telefonu", icon: "📱" },
    { label: "Kulaklık", slug: "bluetooth-kulaklik", icon: "🎧" },
    { label: "Akıllı Saat", slug: "akilli-saat", icon: "⌚" },
    { label: "Laptop", slug: "laptop", icon: "💻" },
    { label: "Robot Süpürge", slug: "robot-supurge", icon: "🧹" },
    { label: "Airfryer", slug: "airfryer", icon: "🍟" },
    { label: "Televizyon", slug: "televizyon", icon: "📺" },
  ],
  "anne-cocuk": [
    { label: "Tümü", slug: "anne-cocuk", icon: "✨" },
    { label: "Bebek Giyim", slug: "bebek-giyim", icon: "👶" },
    { label: "Kız Çocuk", slug: "kiz-cocuk", icon: "👧" },
    { label: "Erkek Çocuk", slug: "erkek-cocuk", icon: "👦" },
    { label: "Oyuncak", slug: "oyuncak", icon: "🧸" },
    { label: "Bebek Arabası", slug: "bebek-arabasi", icon: "🍼" },
  ],
  kozmetik: [
    { label: "Tümü", slug: "kozmetik", icon: "✨" },
    { label: "Parfüm", slug: "parfum", icon: "🌸" },
    { label: "Cilt Bakımı", slug: "cilt-bakimi", icon: "🧴" },
    { label: "Makyaj", slug: "makyaj", icon: "💄" },
  ],
};

const normalizeText = (str: string) =>
  (str || "")
    .replace(/İ/g, "i")
    .replace(/I/g, "i")
    .replace(/ı/g, "i")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .toLowerCase()
    .trim();

export default function CategoryPage() {
  const { slug, categoryId } = useParams<{ slug?: string; categoryId?: string }>();
  const activeSlug = (categoryId || slug || "").toLowerCase().trim();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchProducts();
  }, [activeSlug, searchParams]);

  const fetchProducts = async () => {
    if (!activeSlug) return;

    setLoading(true);
    try {
      let list: any[] = [];
      try {
        const query = supabase
          .from("products")
          .select("*")
          .eq("aktif_mi", true);

        const { data, error }: any = await fetchWithTimeout(query as any, 600);

        if (!error && data && data.length > 0) {
          list = data;
        }
      } catch {
        list = [];
      }

      if (!list || list.length === 0) {
        list = [...verifiedProducts];
      }

      const slugNorm = normalizeText(activeSlug);

      // Kategori Eşleştirmesi
      if (slugNorm === "kadin") {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const g = normalizeText(p.gender || p.cinsiyet);
          const t = normalizeText(p.title);
          return (
            g.includes("kadin") ||
            c.includes("kadin") ||
            t.includes("kadin") ||
            t.includes("elbise") ||
            t.includes("etek") ||
            t.includes("lila")
          );
        });
      } else if (slugNorm === "erkek") {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const g = normalizeText(p.gender || p.cinsiyet);
          const t = normalizeText(p.title);
          return (
            g.includes("erkek") ||
            c.includes("erkek") ||
            t.includes("erkek") ||
            t.includes("tisort") ||
            t.includes("gomlek") ||
            t.includes("jean") ||
            t.includes("pantolon")
          );
        });
      } else if (slugNorm === "elektronik" || slugNorm === "teknoloji") {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const t = normalizeText(p.title);
          return (
            c.includes("elektronik") ||
            t.includes("telefon") ||
            t.includes("iphone") ||
            t.includes("kulaklik") ||
            t.includes("airpods") ||
            t.includes("airtag") ||
            t.includes("sarj") ||
            t.includes("adaptor") ||
            t.includes("mouse") ||
            t.includes("kilif") ||
            t.includes("ekran")
          );
        });
      } else if (
        slugNorm === "ev-yasam" ||
        slugNorm === "mutfak" ||
        slugNorm === "ev yasam"
      ) {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const t = normalizeText(p.title);
          return (
            c.includes("mutfak") ||
            c.includes("airfryer") ||
            c.includes("ev") ||
            t.includes("airfryer") ||
            t.includes("blender") ||
            t.includes("kahve") ||
            t.includes("pisirme") ||
            t.includes("termos") ||
            t.includes("tava") ||
            t.includes("fritoz")
          );
        });
      } else if (slugNorm === "spor") {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const t = normalizeText(p.title);
          return (
            c.includes("spor") ||
            t.includes("ayakkabi") ||
            t.includes("sneaker") ||
            t.includes("kosu") ||
            t.includes("termos") ||
            t.includes("puma") ||
            t.includes("nike") ||
            t.includes("adidas") ||
            t.includes("skechers")
          );
        });
      } else if (slugNorm === "kozmetik") {
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const t = normalizeText(p.title);
          return (
            c.includes("kozmetik") ||
            t.includes("parfum") ||
            t.includes("krem") ||
            t.includes("serum") ||
            t.includes("bakim") ||
            t.includes("deodorant") ||
            t.includes("tiras")
          );
        });
      } else {
        const cleanSlug = normalizeText(activeSlug.replace(/-/g, " "));
        list = list.filter((p) => {
          const c = normalizeText(p.category);
          const t = normalizeText(p.title);
          return (
            c.includes(cleanSlug) ||
            t.includes(cleanSlug) ||
            cleanSlug
              .split(" ")
              .some((part) => part.length > 3 && (c.includes(part) || t.includes(part)))
          );
        });
      }

      // Fiyat filtreleri
      const minPrice = searchParams.get("minPrice");
      const maxPrice = searchParams.get("maxPrice");
      if (minPrice) list = list.filter(p => p.current_price >= parseFloat(minPrice));
      if (maxPrice) list = list.filter(p => p.current_price <= parseFloat(maxPrice));

      // Marka filtreleri
      const brands = searchParams.get("brands");
      if (brands) {
        const brandList = brands.split(",").map(b => b.toLowerCase().trim());
        list = list.filter(p => brandList.includes((p.brand || "").toLowerCase().trim()));
      }

      // Sıralama
      const sort = searchParams.get("sort") || "discount-desc";
      if (sort === "price-asc") {
        list.sort((a, b) => (a.current_price || 0) - (b.current_price || 0));
      } else if (sort === "price-desc") {
        list.sort((a, b) => (b.current_price || 0) - (a.current_price || 0));
      } else if (sort === "newest") {
        list.sort((a, b) => (b.id || 0) - (a.id || 0));
      } else {
        list.sort((a, b) => (b.discount_rate || 0) - (a.discount_rate || 0));
      }

      setProducts(list);
      setTotalCount(list.length);
    } catch (err) {
      console.error("Kategori ürünleri yüklenirken hata:", err);
      setProducts(verifiedProducts as any);
      setTotalCount(verifiedProducts.length);
    } finally {
      setLoading(false);
    }
  };

  const categoryTitle = activeSlug
    ? CATEGORY_TITLES[activeSlug] || activeSlug.replace(/-/g, " ").toUpperCase()
    : "Kategoriler";

  // Ebeveyn kategori veya mevcut kategoriye göre alt çipler
  const currentConfig = activeSlug ? CATEGORY_MAPPING[activeSlug] : null;
  const parentKey = currentConfig?.parentCategory || activeSlug || "kadin";
  const subcategoryList = QUICK_SUBCATEGORIES[parentKey] || QUICK_SUBCATEGORIES["kadin"] || [];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />

      <div className="container mx-auto px-4 py-8 pt-28">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-xs font-semibold text-gray-400 mb-4">
          <Link to="/" className="hover:text-rose-400 transition-colors">Ana Sayfa</Link>
          <span>/</span>
          {currentConfig?.parentCategory && (
            <>
              <Link to={`/kategori/${currentConfig.parentCategory}`} className="hover:text-rose-400 capitalize transition-colors">
                {CATEGORY_TITLES[currentConfig.parentCategory] || currentConfig.parentCategory}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="text-white font-bold">{categoryTitle}</span>
        </div>

        {/* Başlık ve Sayaç */}
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight text-white mb-1">
              {categoryTitle}
            </h1>
            <p className="text-gray-400 text-sm font-medium">
              {loading ? "Fırsatlar yükleniyor..." : `${totalCount} adet indirimli ürün listeleniyor`}
            </p>
          </div>
        </div>

        {/* Hızlı Alt Kategori Çipleri (Subcategory Pills) */}
        {subcategoryList && subcategoryList.length > 0 && (
          <div className="mb-8 overflow-x-auto no-scrollbar py-1">
            <div className="flex items-center gap-2.5 min-w-max">
              {subcategoryList.map((sub) => {
                const isActive = slug === sub.slug;
                return (
                  <Link
                    key={sub.slug}
                    to={`/kategori/${sub.slug}`}
                    className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm ${
                      isActive
                        ? "bg-rose-600 text-white shadow-rose-900/50 scale-105"
                        : "bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700/50"
                    }`}
                  >
                    <span>{sub.icon}</span>
                    <span>{sub.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sol Filtre Paneli */}
          <div className="w-80 flex-shrink-0">
            <AdvancedFilters categorySlug={slug || ""} />
          </div>

          {/* Ürün Listesi */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(9)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-800 rounded-xl h-96 animate-pulse"
                  ></div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-gray-600 mb-4"></i>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Ürün Bulunamadı
                </h3>
                <p className="text-gray-400">
                  Bu kategoride henüz ürün bulunmuyor veya filtrelerinize uygun
                  ürün yok.
                </p>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                data-product-shop
              >
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
